/**
 * Pure helpers for shareable campaign form links.
 *
 * Kept free of React and of extensionless imports so they run under plain `node --test`.
 *
 * The field list mirrors SHAREABLE_STRING_FIELDS / SHAREABLE_BOOLEAN_FIELDS in
 * backend/utils/sharedTemplateConfig.js. The server re-applies its own whitelist, so a
 * drift here can only ever share less, never more.
 */

export const SHAREABLE_STRING_FIELDS = Object.freeze([
  'smtpHost',
  'smtpPort',
  'smtpUser',
  'fromName',
  'fromEmail',
  'subject',
  'message',
  'messageType',
  'customHeaders',
  'customMessageId',
  'inboxPatternId',
  'contentTransferEncoding',
  'returnPath',
  'limit',
  'intervalSeconds',
  'limitToSend'
]);

export const SHAREABLE_BOOLEAN_FIELDS = Object.freeze(['autoImapTest']);

export const SHAREABLE_FIELDS = Object.freeze([...SHAREABLE_STRING_FIELDS, ...SHAREABLE_BOOLEAN_FIELDS]);

/** Human labels for the summary shown before generating and on the shared page. */
export const SHAREABLE_FIELD_LABELS = Object.freeze({
  smtpHost: 'SMTP Host',
  smtpPort: 'SMTP Port',
  smtpUser: 'SMTP Username',
  fromName: 'From Name',
  fromEmail: 'From Email',
  subject: 'Subject',
  message: 'Message',
  messageType: 'Message Type',
  customHeaders: 'Custom Headers',
  customMessageId: 'Custom Message-ID',
  inboxPatternId: 'Inbox Pattern',
  contentTransferEncoding: 'Content Transfer Encoding',
  returnPath: 'Return-Path',
  limit: 'Limit',
  intervalSeconds: 'Interval (seconds)',
  limitToSend: 'Limit to Send',
  autoImapTest: 'Auto IMAP Test'
});

export const SHARE_LINK_STATUS = Object.freeze({
  ACTIVE: 'active',
  EXPIRED: 'expired',
  REVOKED: 'revoked'
});

/**
 * Picks only the shareable fields from the composer form.
 *
 * The SMTP password, recipient files, test recipients and mode are never included.
 */
export function formToTemplateConfig(form) {
  const config = {};
  if (!form || typeof form !== 'object') return config;

  for (const field of SHAREABLE_STRING_FIELDS) {
    const value = form[field];
    if (value === undefined || value === null) continue;
    config[field] = String(value);
  }
  for (const field of SHAREABLE_BOOLEAN_FIELDS) {
    if (form[field] !== undefined) config[field] = Boolean(form[field]);
  }
  return config;
}

/**
 * Returns a new form with the shared values applied.
 *
 * Only supported fields are overwritten. The viewer keeps their own recipients
 * (fileIds, testRecipients) and mode, and the password box is cleared so nothing typed
 * for a different host is carried over. The input form is never mutated.
 */
export function applyTemplateToForm(form, config) {
  const next = { ...(form ?? {}) };
  if (!config || typeof config !== 'object') return next;

  for (const field of SHAREABLE_STRING_FIELDS) {
    const value = config[field];
    if (typeof value === 'string' || typeof value === 'number') next[field] = String(value);
  }
  for (const field of SHAREABLE_BOOLEAN_FIELDS) {
    if (config[field] !== undefined) next[field] = Boolean(config[field]);
  }

  next.smtpPass = '';
  return next;
}

/** Whether any shareable field in the current form already holds a value. */
export function formHasShareableContent(form) {
  if (!form) return false;
  return SHAREABLE_STRING_FIELDS.some((field) => {
    // These two always hold a default, so they are not "content".
    if (field === 'messageType' || field === 'contentTransferEncoding') return false;
    return String(form[field] ?? '').trim() !== '';
  });
}

/** Mirrors backend deriveStatus so the UI can recompute after a local edit. */
export function deriveShareLinkStatus(link, now = new Date()) {
  if (link?.revokedAt) return SHARE_LINK_STATUS.REVOKED;
  if (link?.expiresAt && new Date(link.expiresAt).getTime() <= now.getTime()) {
    return SHARE_LINK_STATUS.EXPIRED;
  }
  return SHARE_LINK_STATUS.ACTIVE;
}

/** Badge tone and label for a link status. */
export function shareLinkStatusBadge(status) {
  switch (status) {
    case SHARE_LINK_STATUS.ACTIVE:
      return { tone: 'success', icon: 'fa-circle-check', label: 'Active' };
    case SHARE_LINK_STATUS.EXPIRED:
      return { tone: 'warning', icon: 'fa-clock', label: 'Expired' };
    case SHARE_LINK_STATUS.REVOKED:
      return { tone: 'danger', icon: 'fa-ban', label: 'Revoked' };
    default:
      return { tone: 'neutral', icon: 'fa-question', label: 'Unknown' };
  }
}

/** Absolute URL for a share path returned by the API, e.g. '/share/abc'. */
export function buildShareUrl(path, origin) {
  return `${String(origin ?? '').replace(/\/+$/, '')}${path}`;
}

/**
 * Converts a Date/ISO string to the value a `<input type="datetime-local">` expects,
 * in the viewer's local time zone. Empty for no value.
 */
export function toDateTimeLocalValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/** Earliest expiry the pickers offer: one minute from now, as a datetime-local value. */
export function minimumExpiryInputValue(now = Date.now()) {
  return toDateTimeLocalValue(new Date(now + 60_000));
}

/** Inverse of toDateTimeLocalValue: '' -> null, otherwise an ISO string. */
export function fromDateTimeLocalValue(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Copies text, falling back to a hidden textarea where the async API is unavailable. */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

/* ── Hand-off from the public share page to the composer ──────────────────────
 *
 * The share page is public, the composer is not. "Open in composer" parks the token
 * here and navigates to the dashboard; if login is needed, the token survives the round
 * trip (including Google OAuth) because sessionStorage is per tab, not per page. The
 * dashboard consumes it exactly once, re-fetches the link (so a revoke in between is
 * honoured) and applies it to the viewer's own draft.
 *
 * Same shape as queuePendingFileId / consumePendingFileIds in campaignFormStorage.js.
 */
const PENDING_SHARED_TEMPLATE_KEY = 'opterite:pending-shared-template';

export function queuePendingSharedTemplate(token) {
  const value = String(token ?? '').trim();
  if (!value) return;
  try {
    window.sessionStorage.setItem(PENDING_SHARED_TEMPLATE_KEY, value);
  } catch {
    /* storage unavailable; the viewer can reopen the link */
  }
}

/** Reads and clears the parked token. Returns '' when there is none. */
export function consumePendingSharedTemplate() {
  try {
    const value = window.sessionStorage.getItem(PENDING_SHARED_TEMPLATE_KEY) ?? '';
    window.sessionStorage.removeItem(PENDING_SHARED_TEMPLATE_KEY);
    return value.trim();
  } catch {
    return '';
  }
}
